import React, { useMemo } from 'react';
import { computeMatchContext, renderHighlightedValue } from '../../shared/utils/matchContext';
import { matchesCanonicalSearch } from '../../shared/searchFilter';

/* Canonical snake_case → DisposalToolList camelCase (transformDisposalTool).
   Canonical fields not produced by transformDisposalTool (tool_name,
   tool_class, oem_tool_number, material) have no entry — they only matter on
   the >=2 server path, which is displayed as-is. part_numbers/asset_tags are
   snake_case pass-throughs (no remap needed). */
const DISPOSAL_FIELD_MAP = {
  supplier_tool_id: 'toolId', alt_tool_id: 'altToolId', tool_code: 'toolCode',
  list_number: 'listNumber', oem: 'oem', tool_type: 'toolType',
  platform: 'platform', part_description: 'partDescription',
  tool_description: 'toolDescription', cspc: 'cspc',
  site_location: 'siteLocation', detail_location: 'detailLocation',
  row_nbr: 'rowNbr', stack_nbr: 'stackNbr', rack_nbr: 'rackNbr',
  shelf_nbr: 'shelfNbr', skid_nbr: 'skidNbr', tier_level: 'tierLevel',
  tier_duns: 'tierDuns', ship_to_location: 'shipToLocation',
  supplier_status: 'supplierStatus',
  preliminary_tool_status: 'preliminaryToolStatus',
  recon_status: 'reconStatus', workflow_status: 'workflowStatus',
};

/* ── OEM badge color config ──────────────────────────────────────────────── */
const OEM_CONFIG = {
  TOYOTA:   { cls: 'dtl-oem-toyota' },
  GM:       { cls: 'dtl-oem-gm' },
  FORD:     { cls: 'dtl-oem-ford' },
  NISSAN:   { cls: 'dtl-oem-nissan' },
  HONDA:    { cls: 'dtl-oem-honda' },
  STELLANTIS: { cls: 'dtl-oem-stellantis' },
};

/* ── Unique values from tools for dynamic filters ────────────────────────── */
function getUniqueValues(tools, key) {
  const set = new Set();
  for (const t of tools) {
    const v = t[key];
    if (v != null && v !== '') set.add(v);
  }
  return [...set].sort();
}

/* ── Sort: OPEN first → list_number asc → incomplete before complete ────── */
function sortTools(tools) {
  return [...tools].sort((a, b) => {
    // 1. recon_status: OPEN first, CLOSED last
    const rsA = a.reconStatus === 'CLOSED' ? 1 : 0;
    const rsB = b.reconStatus === 'CLOSED' ? 1 : 0;
    if (rsA !== rsB) return rsA - rsB;

    // 2. list_number ascending, nulls/empty at bottom
    const lnA = a.listNumber || '';
    const lnB = b.listNumber || '';
    const lnEmptyA = lnA === '' ? 1 : 0;
    const lnEmptyB = lnB === '' ? 1 : 0;
    if (lnEmptyA !== lnEmptyB) return lnEmptyA - lnEmptyB;
    if (lnA !== lnB) return lnA < lnB ? -1 : 1;

    // 3. incomplete before complete
    const compA = a.completeness?.is_complete ? 1 : 0;
    const compB = b.completeness?.is_complete ? 1 : 0;
    return compA - compB;
  });
}

/* The bespoke predicate was replaced by the shared canonical filter
   (src/shared/searchFilter.js) with DISPOSAL_FIELD_MAP for the camelCase
   shape. Server (>=2) results are shown as-is; the gate runs only on the
   sub-min / no-server client path. */

/* ═════════════════════════════════════════════════════════════════════════ */

export default function DisposalToolList({
  tools,
  searchResults = null,
  selectedToolId,
  onToolSelect,
  searchQuery,
  onSearchChange,
  filters,
  onFilterChange,
  loading,
}) {
  /* ── Dynamic filter options ──────────────────────────────────────────── */
  const listNumbers     = useMemo(() => getUniqueValues(tools, 'listNumber'), [tools]);
  const oems            = useMemo(() => getUniqueValues(tools, 'oem'), [tools]);
  const workflowStatuses = useMemo(() => getUniqueValues(tools, 'workflowStatus'), [tools]);
  const toolTypes       = useMemo(() => getUniqueValues(tools, 'toolType'), [tools]);

  /* ── Apply filters + search ─────────────────────────────────────────── */
  const filtered = useMemo(() => {
    // searchResults (server canonical search, query >= MIN) is authoritative,
    // shown as-is (dropdown filters still apply). null = short/no query →
    // filter the loaded list with the shared canonical gate.
    const usingServer = searchResults !== null;
    let result = usingServer ? searchResults : tools;

    if (filters.listNumber) result = result.filter(t => t.listNumber === filters.listNumber);
    if (filters.reconStatus) result = result.filter(t => t.reconStatus === filters.reconStatus);
    if (filters.oem) result = result.filter(t => t.oem === filters.oem);
    if (filters.workflowStatus) result = result.filter(t => t.workflowStatus === filters.workflowStatus);
    if (filters.toolType) result = result.filter(t => t.toolType === filters.toolType);

    if (!usingServer) {
      result = result.filter(t => matchesCanonicalSearch(t, searchQuery, { fieldNameMap: DISPOSAL_FIELD_MAP }));
    }

    return sortTools(result);
  }, [tools, searchResults, filters, searchQuery]);

  /* ── Stats ───────────────────────────────────────────────────────────── */
  const closedCount = useMemo(() => tools.filter(t => t.reconStatus === 'CLOSED').length, [tools]);
  const hasActiveFilters = filters.listNumber || filters.reconStatus || filters.oem || filters.workflowStatus || filters.toolType || searchQuery;

  function clearAll() {
    onFilterChange({ listNumber: '', reconStatus: '', oem: '', workflowStatus: '', toolType: '' });
    onSearchChange('');
  }

  if (loading) {
    return <div className="dtl-loading">Loading tools...</div>;
  }

  return (
    <div className="dtl-container">
      {/* ── Search bar ──────────────────────────────────────────────────── */}
      <div className="dtl-search-wrap">
        <div className="dtl-search-box">
          <input
            type="text"
            className="dtl-search-input"
            placeholder="Search by ID, part number, tag, location..."
            value={searchQuery}
            onChange={e => onSearchChange(e.target.value)}
          />
          {searchQuery && (
            <button
              className="dtl-search-clear"
              onClick={() => onSearchChange('')}
              type="button"
              aria-label="Clear search"
            >
              &times;
            </button>
          )}
        </div>
      </div>

      {/* ── Filter bar ──────────────────────────────────────────────────── */}
      <div className="dtl-filter-bar">
        <select className="dtl-filter-select" value={filters.listNumber}
          onChange={e => onFilterChange({ ...filters, listNumber: e.target.value })}>
          <option value="">All Lists</option>
          {listNumbers.map(ln => <option key={ln} value={ln}>{ln}</option>)}
        </select>

        <select className="dtl-filter-select" value={filters.reconStatus}
          onChange={e => onFilterChange({ ...filters, reconStatus: e.target.value })}>
          <option value="">All Status</option>
          <option value="OPEN">Open</option>
          <option value="CLOSED">Closed</option>
        </select>

        <select className="dtl-filter-select" value={filters.oem}
          onChange={e => onFilterChange({ ...filters, oem: e.target.value })}>
          <option value="">All OEM</option>
          {oems.map(o => <option key={o} value={o}>{o}</option>)}
        </select>

        <select className="dtl-filter-select" value={filters.workflowStatus}
          onChange={e => onFilterChange({ ...filters, workflowStatus: e.target.value })}>
          <option value="">All Workflow</option>
          {workflowStatuses.map(ws => <option key={ws} value={ws}>{ws}</option>)}
        </select>

        <select className="dtl-filter-select" value={filters.toolType}
          onChange={e => onFilterChange({ ...filters, toolType: e.target.value })}>
          <option value="">All Types</option>
          {toolTypes.map(tt => <option key={tt} value={tt}>{tt}</option>)}
        </select>

        {hasActiveFilters && (
          <button className="dtl-filter-clear" onClick={clearAll} type="button">
            Clear
          </button>
        )}
      </div>

      {/* ── Record count ────────────────────────────────────────────────── */}
      <div className="dtl-summary-line">
        Showing {filtered.length} of {tools.length} tools &middot; {closedCount} closed
      </div>

      {/* ── Tool list ───────────────────────────────────────────────────── */}
      <div className="dtl-list">
        {filtered.length === 0 && tools.length > 0 && (
          <div className="dtl-empty">
            No tools match the current filters. Try clearing filters or selecting a different site.
          </div>
        )}
        {filtered.length === 0 && tools.length === 0 && (
          <div className="dtl-empty">No tools found for this site.</div>
        )}

        {filtered.map((tool, index) => {
          const id = tool.id;
          const isActive = selectedToolId === id;
          const isComplete = tool.completeness?.is_complete;
          const missingFields = tool.completeness?.missing_fields || [];
          const oemCfg = OEM_CONFIG[(tool.oem || '').toUpperCase()] || { cls: 'dtl-oem-default' };
          const matchCtx = computeMatchContext(tool, searchQuery);

          return (
            <button
              key={id}
              className={`dtl-row${isActive ? ' active' : ''}`}
              onClick={() => onToolSelect(tool)}
            >
              {/* Top line: row number + ID + badges */}
              <div className="dtl-row-top">
                <span className="dtl-row-num">{index + 1}</span>
                <span className="dtl-tool-id">{tool.toolId || id}</span>
                {tool.altToolId && (
                  <span className="dtl-alt-id">{tool.altToolId}</span>
                )}
                <div className="dtl-badges">
                  {tool.oem && (
                    <span className={`dtl-badge ${oemCfg.cls}`}>{tool.oem}</span>
                  )}
                  <span className={`dtl-badge ${tool.reconStatus === 'CLOSED' ? 'dtl-recon-closed' : 'dtl-recon-open'}`}>
                    {tool.reconStatus === 'CLOSED' ? 'Closed' : 'Open'}
                  </span>
                </div>
              </div>

              {/* Supplier name */}
              {tool.supplier_name && (
                <div className="dtl-supplier">{tool.supplier_name}</div>
              )}

              {/* Completeness indicator */}
              <div className="dtl-completeness">
                {isComplete ? (
                  <span className="dtl-complete-ok">&#10003; Complete</span>
                ) : (
                  <span className="dtl-complete-missing">
                    Missing: {missingFields.join(', ')}
                  </span>
                )}
              </div>

              {/* Match context badge — shows which field matched the search */}
              {matchCtx && (
                <div className="dtl-match-context">
                  <span style={{ fontWeight: 'var(--font-normal)', opacity: 0.7 }}>Matched:&nbsp;</span>
                  {matchCtx.label} &middot; {renderHighlightedValue(matchCtx.value, searchQuery)}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
